import { useCallback, useEffect, useRef, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, X } from 'lucide-react';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { pricingRulesApi } from '../services/pricing-rules-api';
import type { ScopeProduct } from '../types/pricing-rules.types';

interface Props {
  selected: string[];
  onChange: (skus: string[]) => void;
  /** Spec 294 (D-R4-6): elegir uno solo (el límite en Bs); elegir otro reemplaza. */
  unoSolo?: boolean;
}

type Estado = 'QUIETO' | 'BUSCANDO' | 'ERROR';

export function SkuAutocomplete({ selected, onChange, unoSolo = false }: Props) {
  const [abierto, setAbierto] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ScopeProduct[]>([]);
  const [estado, setEstado] = useState<Estado>('QUIETO');
  const [error, setError] = useState<unknown>(null);
  const [nombres, setNombres] = useState<Map<string, ScopeProduct>>(() => new Map());
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Cada pedido toma un número; al volver, si no es el último, no escribe nada
  // (una respuesta vieja no pisa la nueva).
  const pedidoRef = useRef(0);
  const nombresRef = useRef(nombres);
  nombresRef.current = nombres;

  const recordar = useCallback((productos: ScopeProduct[]) => {
    if (productos.length === 0) return;
    setNombres((m) => {
      const nuevo = new Map(m);
      for (const p of productos) nuevo.set(p.sku, p);
      return nuevo;
    });
  }, []);

  const pedir = useCallback(
    async (q: string) => {
      const n = ++pedidoRef.current;
      setEstado('BUSCANDO');
      try {
        const data = await pricingRulesApi.productosParaElegir(q);
        if (n !== pedidoRef.current) return;
        setResults(data);
        recordar(data);
        setEstado('QUIETO');
      } catch (err) {
        if (n !== pedidoRef.current) return;
        setError(err);
        setEstado('ERROR');
      }
    },
    [recordar]
  );

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    []
  );

  // Los elegidos sin nombre conocido (una regla guardada): se piden por su código.
  // Es una ayuda: si falla, se ve el código solo, sin aviso.
  const clave = selected.join(',');
  useEffect(() => {
    const faltan = selected.filter((s) => !nombresRef.current.has(s));
    if (faltan.length === 0) return;
    let vigente = true;
    Promise.resolve()
      .then(() => pricingRulesApi.productosPorCodigo(faltan))
      .then((data) => {
        if (vigente) recordar(data);
      })
      .catch(() => {});
    return () => {
      vigente = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave, recordar]);

  const alEnfocar = () => {
    setAbierto(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    void pedir(query);
  };

  const alTipear = (q: string) => {
    setQuery(q);
    setAbierto(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // Durante el debounce ya se está buscando el texto nuevo: ni «Sin resultados»
    // ni la lista vieja bajo el texto nuevo. Una respuesta en vuelo queda vieja.
    pedidoRef.current++;
    setEstado('BUSCANDO');
    debounceRef.current = setTimeout(() => void pedir(q), 300);
  };

  const add = (product: ScopeProduct) => {
    onChange(unoSolo ? [product.sku] : [...selected, product.sku]);
    recordar([product]);
    setQuery('');
    if (unoSolo) setAbierto(false);
  };

  const remove = (sku: string) => onChange(selected.filter((s) => s !== sku));

  const visibles = results.filter((p) => !selected.includes(p.sku));

  return (
    <div className="space-y-3">
      <div
        className="space-y-3"
        onKeyDown={(e) => {
          if (e.key === 'Escape') setAbierto(false);
        }}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setAbierto(false);
        }}
      >
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            aria-label="Buscar producto"
            className="pl-9"
            placeholder="Buscar producto por nombre o código…"
            value={query}
            onFocus={alEnfocar}
            onChange={(e) => alTipear(e.target.value)}
          />
        </div>

        {abierto && (
          <div className="rounded-md border max-h-48 overflow-y-auto">
            {estado === 'BUSCANDO' && (
              <div className="p-3 text-center text-xs text-muted-foreground">Buscando…</div>
            )}
            {estado === 'ERROR' && (
              <div className="flex items-center justify-between gap-3 p-3 text-sm">
                <span>{mensajeDeError(error, 'No se pudo buscar productos.')}</span>
                <Button variant="outline" size="sm" onClick={() => void pedir(query)}>
                  Reintentar
                </Button>
              </div>
            )}
            {estado === 'QUIETO' && visibles.length === 0 && (
              <div className="p-3 text-center text-xs text-muted-foreground">
                {query ? `Sin resultados para «${query}».` : 'No hay productos activos.'}
              </div>
            )}
            {estado === 'QUIETO' &&
              visibles.map((p) => (
                <button
                  key={p.sku}
                  type="button"
                  onClick={() => add(p)}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm border-b last:border-b-0 hover:bg-muted/50 transition-colors text-left"
                >
                  <div className="min-w-0">
                    <div className="font-medium [overflow-wrap:anywhere]">{p.name}</div>
                    {p.category && (
                      <div className="text-xs text-muted-foreground">{p.category}</div>
                    )}
                  </div>
                  <Badge variant="outline" className="text-xs ml-2 shrink-0">
                    {p.sku}
                  </Badge>
                </button>
              ))}
          </div>
        )}
      </div>

      {selected.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">
            {unoSolo ? 'Elegido' : `Seleccionados (${selected.length})`}
          </p>
          <div className="space-y-1">
            {selected.map((sku) => {
              const p = nombres.get(sku);
              return (
                <div
                  key={sku}
                  className="flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm"
                >
                  <span className="min-w-0">
                    {p && (
                      <>
                        <span className="font-medium [overflow-wrap:anywhere]">{p.name}</span>
                        {' · '}
                      </>
                    )}
                    <span className="text-xs text-muted-foreground">{sku}</span>
                    {p?.activo === false && (
                      <span className="text-xs text-muted-foreground"> (inactivo)</span>
                    )}
                  </span>
                  <button
                    type="button"
                    aria-label={'Sacar ' + sku}
                    onClick={() => remove(sku)}
                    className="text-destructive hover:text-destructive/80 ml-auto"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>
          {!unoSolo && (
            <p className="text-xs text-muted-foreground">
              {selected.length} {selected.length === 1 ? 'SKU seleccionado' : 'SKUs seleccionados'}{' '}
              · {selected.length} {selected.length === 1 ? 'producto' : 'productos'} en alcance
            </p>
          )}
        </div>
      )}
    </div>
  );
}
